import React from "react";
import LegalPage, { P, Section, List, Mail } from "../../components/LegalPage";
import {
  COMPANY,
  SUPPORT_EMAIL,
  TERMS_UPDATED,
  GOVERNING_STATE,
  MINIMUM_AGE,
} from "../../data/legalInfo";

export default function Terms() {
  return (
    <LegalPage title="Terms of Service" subtitle={`Last updated ${TERMS_UPDATED}`}>
      <P>
        These terms are an agreement between you and {COMPANY} covering your use of Lineup, a
        fantasy college football game available on the web and as a mobile app. By creating an
        account you agree to them. If you do not agree, do not use Lineup.
      </P>

      <Section title="Who can use Lineup">
        <P>
          You must be at least {MINIMUM_AGE} years old and able to enter a binding contract. You may
          hold one account. Everything done through your account is your responsibility, so keep
          your password to yourself and tell us at <Mail address={SUPPORT_EMAIL} /> if you think
          someone else has access to it.
        </P>
      </Section>

      <Section title="What Lineup is">
        <P>
          Lineup is a free, skill-based fantasy game. You draft real college football teams and earn
          points based on how those teams perform in real games. There is no entry fee, no wagering,
          no prize pool, and no cash payout of any kind. Lineup is not a gambling service and does
          not facilitate one.
        </P>
        <P>
          Lineup is not affiliated with, endorsed by, or sponsored by the NCAA, any conference, any
          school, ESPN, or any other organization whose names or results appear in the game. Team
          names and results are used to describe real events.
        </P>
      </Section>

      <Section title="Leagues and commissioners">
        <P>
          Leagues are private and are run by the manager who created them, along with any co-admins
          they appoint. Commissioners can set league rules, run the draft, enter picks in an offline
          draft, and correct entries. If you have a dispute about how a league is being run, take it
          up with your commissioner. We do not arbitrate league disputes.
        </P>
        <P>
          Any arrangement your league makes outside Lineup, including buy-ins, side bets, or prizes,
          is entirely between you and the other people in it. We are not a party to it, we take no
          fee from it, and we take no responsibility for it.
        </P>
      </Section>

      <Section title="Acceptable use">
        <P>You agree not to:</P>
        <List
          items={[
            "Use another person's account, or create an account for someone else without their permission.",
            "Create multiple accounts to gain an advantage in a league.",
            "Harass, threaten, or abuse other managers, including through profile text and smack talk.",
            "Upload content you do not have the right to use, or content that is unlawful, hateful, or obscene.",
            "Attempt to access data belonging to other users, probe our systems for vulnerabilities, or interfere with the service.",
            "Scrape, automate, or resell access to Lineup.",
          ]}
        />
      </Section>

      <Section title="Content you provide">
        <P>
          You keep ownership of the profile images and text you add. By adding them you give us
          permission to store and display them within Lineup for the purpose of running the service.
          We can remove content that breaks these terms.
        </P>
        <P>
          There is no tolerance for objectionable content or abusive managers. Team names, avatar
          images and smack talk that are hateful, harassing, obscene, or that target a person or a
          group are not allowed, and accounts that post them can be suspended or removed without
          notice.
        </P>
      </Section>

      <Section title="Reporting and blocking">
        <P>
          Open a manager's card on the league standings and choose Report or block. Reporting sends
          us the content and your description of the problem. We review every report and act on it
          within 24 hours, which can mean removing the content, suspending the manager, or both.
        </P>
        <P>
          Blocking is immediate and needs no review. A blocked manager's team name, avatar and smack
          talk stop being visible to you. They keep their place in the standings and continue to
          score points, because removing them from a league would change the season for everyone
          else in it.
        </P>
      </Section>

      <Section title="Scoring and game data">
        <P>
          Scores, schedules, and spreads come from third-party providers and are ingested
          automatically. Data can be late, incomplete, or wrong, and games get rescheduled or
          cancelled. We correct scoring errors when we find them, and corrections can change
          standings after the fact. We do not guarantee that any score, point total, or standing is
          accurate at any given moment.
        </P>
      </Section>

      <Section title="Availability">
        <P>
          Lineup is provided as it is. We do not promise it will be available without interruption,
          and we may change, suspend, or discontinue features. We will give reasonable notice in the
          app before removing something managers rely on during a season.
        </P>
      </Section>

      <Section title="Ending your account">
        <P>
          You can delete your account at any time from your account settings. We can suspend or
          terminate an account that breaks these terms. If your account ends mid-season, your
          leagues continue without you.
        </P>
      </Section>

      <Section title="Disclaimers and liability">
        <P>
          To the fullest extent the law allows, Lineup is provided without warranties of any kind,
          express or implied, including any warranty of merchantability, fitness for a particular
          purpose, or non-infringement.
        </P>
        <P>
          To the fullest extent the law allows, {COMPANY} is not liable for indirect, incidental,
          special, or consequential damages arising from your use of Lineup. Because Lineup is free,
          our total liability to you for any claim is limited to one hundred United States dollars.
        </P>
      </Section>

      <Section title="Changes to these terms">
        <P>
          We will update the date at the top of this page when these terms change, and we will
          notify you in the app before a material change takes effect. Continuing to use Lineup
          after that means you accept the updated terms.
        </P>
      </Section>

      <Section title="Governing law">
        <P>
          These terms are governed by the laws of the State of {GOVERNING_STATE}, without regard to
          its conflict of law rules. Any dispute will be brought in the state or federal courts
          located in {GOVERNING_STATE}, and both of us consent to that venue.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about these terms: <Mail address={SUPPORT_EMAIL} />.
        </P>
      </Section>
    </LegalPage>
  );
}
